import { useRef, useEffect, createRef, useState } from 'react'
import { debounce } from 'lodash'
import type { RefObject } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import Text from 'components/ui/text'
import { cn } from 'utils/classMerge'
import { translate } from 'utils/translation'

import { Badge } from '../badge'
import Select, { SelectOption } from '../select'

import { TabButton } from './components/button'

const tabVariants = cva('tw-hidden ', {
  variants: {
    variant: {
      default:
        'tw-border-b tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-black/10 tw-bg-white sm:tw-block',
      'default-only-lg': '',
      button:
        'tw-rounded-xl tw-border tw-border-solid tw-border-black/10 tw-bg-white tw-shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),0px_1px_2px_-1px_rgba(0,0,0,0.10)] sm:tw-block',
      control: 'tw-rounded-xl tw-bg-black/5 tw-p-1 sm:tw-block',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const buttonsVariants = cva('tw-relative tw-flex tw-items-center', {
  variants: {
    variant: {
      default: '',
      'default-only-lg': 'tw-gap-8 tw-text-zinc-700',
      control: '',
      button:
        'tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-black/10',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const activeVariants = cva('tw-absolute tw-transition-all tw-duration-300', {
  variants: {
    variant: {
      default: 'tw--bottom-px tw-h-0.5 tw-bg-orange-500',
      'default-only-lg': 'tw--bottom-px tw-h-0.5 tw-bg-orange-500 lg:tw-block',
      button: 'tw-bottom-0 tw-h-0.5 tw-bg-orange-500',
      control: 'tw-h-full tw-rounded-lg tw-bg-white',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const textVariants = cva(
  'tw-m-0 tw-flex tw-items-center tw-gap-3 tw-p-3 tw-text-sm tw-font-medium  tw-transition-[color] tw-duration-300',
  {
    variants: {
      variant: {
        default: 'tw-p-3 tw-text-orange-500',
        'default-only-lg': 'tw-px-0 tw-py-3 tw-text-orange-600',
        button: 'tw-p-3 tw-text-orange-500',
        control: 'tw-p-3 tw-text-orange-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

interface TabsProps extends VariantProps<typeof tabVariants> {
  /** Array of options for the tabs, where each option has a name, value, and optional badge */
  options: SelectOption[]
  /** The selected tab option */
  selected: SelectOption
  /** Callback function that is called when a tab is changed */
  onChange: (option: SelectOption) => void
  /** Optional class name for styling the tabs container */
  className?: string
  /** Optional class name for styling the mobile version of the tabs */
  mobileClassName?: string
  /** Optional flag to make the tabs full-width on larger screens */
  isFullWidth?: boolean
  /** Optional test ID for the tabs container */
  testId?: string
  /** Optional flag to color the active badge */
  shouldColorActiveBadge?: boolean
}

const calculateIndicatorPosition = (
  tabRefs: RefObject<HTMLButtonElement>[],
  selected: SelectOption,
  options: SelectOption[],
  variant: string,
) => {
  const selectedIndex = options.findIndex(
    (option) => option.value === selected.value,
  )
  const tabWidths = tabRefs.map((ref) => ref.current?.offsetWidth || 0)

  const indicatorLeft = tabWidths
    .slice(0, selectedIndex)
    .reduce((acc, width) => {
      if (variant === 'default-only-lg') {
        return acc + width + 32
      }
      return acc + width
    }, 0)

  return {
    indicatorWidth: tabWidths[selectedIndex] || 0,
    indicatorLeft,
  }
}

const Tabs = ({
  options,
  selected,
  onChange,
  className,
  variant,
  isFullWidth = false,
  testId = 'tabs',
  mobileClassName = '',
  shouldColorActiveBadge = false,
}: TabsProps) => {
  const tabRefs = useRef<RefObject<HTMLButtonElement>[]>(
    options.map(() => createRef()),
  )
  const [indicatorStyle, setIndicatorStyle] = useState<{
    width: number
    left: number
  }>({ width: 0, left: 0 })

  const selectedRef = useRef(selected)

  const updateIndicatorPosition = () => {
    const { indicatorWidth, indicatorLeft } = calculateIndicatorPosition(
      tabRefs.current,
      selectedRef.current,
      options,
      variant || 'default',
    )
    setIndicatorStyle({ width: indicatorWidth, left: indicatorLeft })
  }

  useEffect(() => {
    selectedRef.current = selected
    updateIndicatorPosition()
  }, [selected.value])

  useEffect(() => {
    const debouncedResizeHandler = debounce(() => {
      updateIndicatorPosition()
    }, 200)
    window.addEventListener('resize', debouncedResizeHandler)
    return () => window.removeEventListener('resize', debouncedResizeHandler)
  }, [])

  return (
    <>
      <Select
        options={options}
        outsideValue={selected}
        onChange={onChange}
        className={cn('sm:tw-hidden', mobileClassName)}
        data-testid={`${testId}-select`}
      />
      <div
        className={cn(tabVariants({ variant, className }))}
        data-testid={testId}
      >
        <div
          role="tablist"
          aria-label="tabs"
          className={cn(buttonsVariants({ variant }))}
        >
          {indicatorStyle.width > 0 && (
            <div
              className={cn(activeVariants({ variant }))}
              style={{
                left: `${indicatorStyle.left}px`,
                width: `${indicatorStyle.width}px`,
              }}
            />
          )}

          {options.map((option, index) => {
            const { name, value, badge } = option
            const isSelected = selected.value === value

            return (
              <TabButton
                key={name}
                ref={tabRefs.current[index]}
                data-testid={`${testId}-tab-${index}`}
                role="tab"
                variant={variant}
                type="button"
                className={cn({
                  'tw-cursor-default': isSelected,
                  'tw-w-full tw-border-r-0': isFullWidth,
                })}
                onClick={() => {
                  if (option.value === selected.value) return
                  onChange(option)
                }}
              >
                <Text
                  as="span"
                  className={cn(textVariants({ variant }), {
                    'tw-text-zinc-700': !isSelected,
                  })}
                  renderChildren
                >
                  {option.icon && (
                    <option.icon
                      data-testid={`${testId}-tab-${index}-icon`}
                      width={20}
                      height={20}
                      className={cn(
                        'tw-text-orange-500 tw-transition-[color] tw-duration-300',
                        {
                          'tw-text-zinc-400': !isSelected,
                        },
                      )}
                    />
                  )}
                  {translate(name)}
                  {badge && (
                    <Badge
                      {...badge}
                      variant={
                        shouldColorActiveBadge && isSelected
                          ? 'orange50'
                          : 'default'
                      }
                    />
                  )}
                </Text>
              </TabButton>
            )
          })}
        </div>
      </div>
    </>
  )
}

export { Tabs, tabVariants, activeVariants, buttonsVariants }
